import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, LogIn } from "lucide-react";
import Logo from "./Logo";

const APP_LOGIN_URL = "https://my.cleany.dk";

const links = [
    { href: "/#moduler", label: "Moduler" },
    { href: "/#sadan-virker-det", label: "Sådan virker det" },
    { href: "/#priser", label: "Priser" },
    { href: "/#faq", label: "FAQ" },
    { href: "/updates", label: "Updates", route: true },
    { href: "/#om-os", label: "Om os" },
];

export default function Navbar({ onCtaClick }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="navbar"
            className={`fixed top-9 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/75 backdrop-blur-xl border-b border-slate-200/80"
                    : "bg-transparent"
            }`}
        >
            <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center" data-testid="navbar-logo-link">
                    <Logo />
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {links.map((l) =>
                        l.route ? (
                            <Link
                                key={l.href}
                                to={l.href}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                {l.label}
                            </a>
                        ),
                    )}
                </div>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        href={APP_LOGIN_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="navbar-app-login"
                        className="inline-flex items-center gap-1.5 rounded-full text-slate-600 hover:text-slate-900 text-sm font-medium px-4 h-10 transition-colors"
                    >
                        <LogIn className="h-4 w-4" />
                        Log ind
                    </a>
                    <button
                        onClick={onCtaClick}
                        data-testid="navbar-cta-waitlist"
                        className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-5 h-10 transition-all active:scale-[0.97]"
                    >
                        Tilmeld launch
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    </button>
                </div>

                <button
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100"
                    aria-label="Åbn menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </nav>

            {open && (
                <div className="md:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-3">
                        {links.map((l) =>
                            l.route ? (
                                <Link
                                    key={l.href}
                                    to={l.href}
                                    onClick={() => setOpen(false)}
                                    className="text-base font-medium text-slate-700 py-2"
                                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            ) : (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="text-base font-medium text-slate-700 py-2"
                                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </a>
                            ),
                        )}
                        <button
                            onClick={() => {
                                setOpen(false);
                                onCtaClick();
                            }}
                            data-testid="mobile-cta-waitlist"
                            className="mt-2 rounded-full bg-slate-900 text-white font-medium h-11 px-5"
                        >
                            Tilmeld launch
                        </button>
                        <a
                            href={APP_LOGIN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-app-login"
                            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 text-slate-700 font-medium h-11 px-5"
                        >
                            <LogIn className="h-4 w-4" />
                            Log ind på Cleany
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
